import React from 'react'
import { observer } from 'mobx-react'
import {
  SingleItemSelector,
  SelectItem,
} from 'admin/staffing/components/WorkspacePostingDialog/SingleItemSelector'
import Avatar from 'components/Avatar'
import Button from 'components/Button'
import {
  CustomSelect,
  ItemSource,
  useCustomSelectStateFromItemSource,
} from 'components/inputs/CustomSelect'
import { Form, FormGroupContainer, FormRow } from 'components/inputs'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentDescription,
  ModalSheetContentTitle,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetSection,
} from 'components/ModalSheet'
import { RadioButtonGroup, RadioButtonItem } from 'components/RadioButtonGroup'
import Skill from 'iam/models/Skill'
import {
  ManageSkillsDialogTab,
  ManageSkillsViewModel,
} from './ManageSkillsViewModel'
import styles from './ManageSkillsDialog.sass'

interface ManageSkillsDialogProps {
  vm: ManageSkillsViewModel
}

export const ManageSkillsDialog = observer(
  ({ vm }: ManageSkillsDialogProps) => {
    return (
      <ModalSheet
        showing={vm.showing}
        onRequestClose={vm.close}
        disableCloseOnOutsideClick
      >
        <ModalSheetHeader withCloseButton />

        <ModalSheetContent>
          <ModalSheetSection horizontallySpaced>
            <ModalSheetContentTitle>Manage skills</ModalSheetContentTitle>
            <ModalSheetContentDescription compact>
              Add or remove skills from{' '}
              {vm.selectedUser ? (
                <span className={styles.headerName}>
                  {vm.selectedUser.displayName}
                </span>
              ) : (
                'the selected users'
              )}
              {vm.selectedWorkspace && (
                <>
                  {' in '}
                  <span className={styles.headerName}>
                    {vm.selectedWorkspace.name}
                  </span>
                </>
              )}
              .
            </ModalSheetContentDescription>
          </ModalSheetSection>

          <ModalSheetSection spaced>
            <Form id="manageSkills" onSubmit={vm.submit}>
              <FormRow>
                <FormGroupContainer>
                  <RadioButtonGroup>
                    <RadioButtonItem
                      className={styles.tabButton}
                      onClick={() =>
                        vm.setSelectedTab(ManageSkillsDialogTab.Add)
                      }
                      active={vm.selectedTab === ManageSkillsDialogTab.Add}
                      disabled={vm.submit.pending}
                      dataTest="manage-skills-tab-add"
                    >
                      Add
                    </RadioButtonItem>
                    <RadioButtonItem
                      className={styles.tabButton}
                      onClick={() =>
                        vm.setSelectedTab(ManageSkillsDialogTab.Remove)
                      }
                      active={vm.selectedTab === ManageSkillsDialogTab.Remove}
                      disabled={vm.submit.pending}
                      dataTest="manage-skills-tab-remove"
                    >
                      Remove
                    </RadioButtonItem>
                  </RadioButtonGroup>
                </FormGroupContainer>
              </FormRow>

              {!vm.selectedWorkspace && (
                <FormRow>
                  <FormGroupContainer
                    label="Workspace"
                    error={vm.validation.getError('workspace')}
                  >
                    <SingleItemSelector
                      model={vm.workspace}
                      itemOptions={vm.workspaces}
                      itemPrefix={(item) => (
                        <Avatar
                          subject={{
                            avatar: item?.branding.logos.icon,
                          }}
                          size={24}
                        />
                      )}
                      onChange={(value) =>
                        vm.setWorkspace(value === undefined ? null : value)
                      }
                      itemToString={(item) => (item ? item.name : '')}
                      placeholder="Select workspace"
                      disabled={vm.submit.pending}
                      dataTest="manage-skills-workspace-selector"
                    />
                  </FormGroupContainer>
                </FormRow>
              )}

              <FormRow>
                <FormGroupContainer
                  label="Skills"
                  error={vm.validation.getError('skills')}
                >
                  <SkillsSelector
                    vm={vm}
                    disabled={vm.submit.pending}
                    dataTest="manage-skills-selector"
                  />
                </FormGroupContainer>
              </FormRow>
            </Form>
          </ModalSheetSection>
        </ModalSheetContent>

        <ModalSheetFooter>
          <ModalSheetFooterSpacer />
          <ModalSheetFooterButtonGroup>
            <Button type="default" onClick={vm.close}>
              Close
            </Button>
            <Button
              submit
              form="manageSkills"
              type="primary"
              disabled={vm.submit.pending}
              dataTest="manage-skills-submit"
            >
              Save
            </Button>
          </ModalSheetFooterButtonGroup>
        </ModalSheetFooter>
      </ModalSheet>
    )
  },
)

interface SkillsSelectorProps {
  vm: ManageSkillsViewModel
  disabled?: boolean
  dataTest?: string
}

const SkillsSelector = observer(function ({
  vm,
  disabled,
  dataTest,
}: SkillsSelectorProps) {
  const itemToString = (item: Skill | undefined) => item?.name || ''

  const itemSource = React.useMemo(
    () =>
      ItemSource.static(vm.availableSkills, (item, text) =>
        item.name.toLowerCase().includes(text.toLowerCase()),
      ),
    [vm.availableSkills],
  )

  const change = React.useCallback(
    (skills: Skill[]) => (skills ? vm.setSkills(skills) : undefined),
    [vm],
  )

  const itemProps = useCustomSelectStateFromItemSource({
    itemSource,
  })

  return (
    <CustomSelect
      {...itemProps}
      itemToString={itemToString}
      value={vm.skills}
      onChange={change}
      disabled={disabled}
      multi={true}
      persist
      placeholder="Select skills"
      data-test={dataTest}
      renderSearchItem={({ item, ...itemProps }) => (
        <SelectItem key={item.id} {...itemProps}>
          {itemToString(item)}
        </SelectItem>
      )}
    />
  )
})
